import React, { useState } from "react";
import { Button, Space, Tooltip, Modal, Typography, Table } from "antd";
import {
  ShareAltOutlined,
  MailOutlined,
  WhatsAppOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import {
  deleteDocument,
  fetchDocument,
} from "../../Redux/Slice/Patient/uploadDoc";

const { Text } = Typography;

const DocumentTable = ({ documentData, patientId }) => {
  const dispatch = useDispatch();
  const [shareModal, setShareModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);

  const handleShare = (record) => {
    setSelectedDoc(record);
    setShareModal(true);
  };

  const handleEmailShare = () => {
    if (!selectedDoc?.filePath) return;
    const subject = encodeURIComponent("Shared Document");
    const body = encodeURIComponent(
      `Please find the document link: ${selectedDoc.filePath}`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`);
    setShareModal(false);
  };

  const handleWhatsAppShare = () => {
    if (!selectedDoc?.filePath) return;
    const message = encodeURIComponent(
      `Here is the document link: ${selectedDoc.filePath}`
    );
    window.open(`https://wa.me/?text=${message}`, "_blank");
    setShareModal(false);
  };

  const handleDelete = (record) => {
    setSelectedDoc(record);
    setDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedDoc?.documentId) return;

    try {
      await dispatch(deleteDocument(selectedDoc.documentId)).unwrap();
      toast.success(
        `Document "${selectedDoc?.fileName}" deleted successfully!`
      );

      // 🔹 Refresh the documents list
      dispatch(fetchDocument({ patientId }));
    } catch (error) {
      toast.error("Failed to delete document");
    } finally {
      setDeleteModal(false);
      setSelectedDoc(null);
    }
  };

  const cancelDelete = () => {
    toast.info("Delete cancelled");
    setDeleteModal(false);
    setSelectedDoc(null);
  };

  const documentColumn = [
    { title: "File Name", dataIndex: "fileName" },
    {
      title: "File Path",
      dataIndex: "filePath",
      render: (text) =>
        text ? (
          <a href={text} target="_blank" rel="noopener noreferrer">
            {text}
          </a>
        ) : (
          "-"
        ),
    },
    { title: "Doc Type", dataIndex: "docType" },
    { title: "Created At", dataIndex: "createdAt" },
    { title: "Status", dataIndex: "status" },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space>
          {record.filePath ? (
            <>
              <Tooltip title="Share Document">
                <Button
                  icon={<ShareAltOutlined />}
                  onClick={() => handleShare(record)}
                />
              </Tooltip>
              <Tooltip title="Delete">
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(record)}
                />
              </Tooltip>
            </>
          ) : (
            "-"
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Table
        columns={documentColumn}
        dataSource={documentData?.data || []}
        rowKey={(r, i) => r.documentId || i}
        bordered
      />

      {/* Share Modal */}
      <Modal
        open={shareModal}
        title="Share Document"
        onCancel={() => setShareModal(false)}
        footer={null}
      >
        <Text strong>Select a sharing option:</Text>
        <Space style={{ marginTop: 16 }}>
          <Button
            type="primary"
            icon={<MailOutlined />}
            onClick={handleEmailShare}
          >
            Email
          </Button>
          <Button
            style={{ backgroundColor: "#25D366", color: "white" }}
            icon={<WhatsAppOutlined />}
            onClick={handleWhatsAppShare}
          >
            WhatsApp
          </Button>
        </Space>
      </Modal>

      {/* Delete Modal */}
      <Modal
        open={deleteModal}
        title="Confirm Delete"
        onOk={confirmDelete}
        onCancel={cancelDelete}
        okText="Yes, Delete"
        okType="danger"
        cancelText="Cancel"
      >
        <Text>
          Are you sure you want to delete{" "}
          <strong>{selectedDoc?.fileName}</strong>?
        </Text>
      </Modal>
    </>
  );
};

export default DocumentTable;
